import { useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import './bookingI18n';
import { IconBuilding, IconChevronsLeft, IconChevronsRight, IconClockHour4, IconMail, IconPaperclip, IconRefresh, IconSearch, IconStar } from '@tabler/icons-react';
import { Segmented } from '@hana/ui';
import { isUnMail, mailHasSla, mailJob, mailLabelCounts, mailScopeCounts, MAILS, queryMails, type Mail, type MailScope } from './mail';
import type { BookingRequest } from './types';
import s from './inbox.module.css';

// Hộp thư info@ cạnh danh sách yêu cầu (Mint-Booking, 320 ⇄ rail 52). Chỉ đọc: nút ghi báo toast ở màn cha.
const SCOPES: ReadonlyArray<{ key: MailScope; label: string; tip: string }> = [
  { key: 'mine', label: 'Của tôi', tip: 'Thư mang tag PIC của tôi (gồm job tôi đang thay)' },
  { key: 'un', label: 'Chưa PIC', tip: 'PIC:UNASSIGNED · chỉ người điều phối hôm nay được động vào' },
  { key: 'all', label: 'Tất cả', tip: 'Mọi thư chưa Solved' },
  { key: 'solved', label: 'Solved', tip: 'Thư đã xử lý · mở lại được' },
];
const LABEL_COLOR: Record<string, string> = {
  BOOKING: 'var(--d-blue)',
  LICH_TAU: 'var(--d-amber)',
  NOTICE: 'var(--d-violet)',
  SI_VGM: 'var(--d-green)',
  URGENT_CASE: 'var(--badge)',
  CHUNG_TU: 'var(--d-blue)',
};
const labelColor = (l: string) => LABEL_COLOR[l] ?? 'var(--d-gray)';
const L_TIP = ['', 'Gắn 1 job', 'Cấp tàu / chuyến', 'Tin chung hãng', 'Chi phí phát sinh', 'Nhiệt độ / LOI · khẩn', 'Không cần đọc'];
const LTYPE: Record<string, number> = { BOOKING: 1, SI_VGM: 1, CHUNG_TU: 1, LENH_CONT: 1, GIAO_HANG: 1, LICH_TAU: 2, NOTICE: 3, DnD_PWC: 4, URGENT_CASE: 5, THANH_TOAN: 6, KHAC: 6 };
const typeOf = (m: Mail) => (m.kind === 'none' ? 6 : (LTYPE[m.label] ?? 1));

const preview = (m: Mail) => m.body.replace(/\s+/g, ' ').slice(0, 160);

export function InboxPane({
  me,
  requests,
  open,
  onToggle,
  onJob,
  onNew,
  onWrite,
}: {
  me: string;
  requests: readonly BookingRequest[];
  open: boolean;
  onToggle: () => void;
  /** lọc danh sách yêu cầu theo mã trong tiêu đề */
  onJob: (job: string) => void;
  onNew: () => void;
  onWrite: () => void;
}) {
  const t = useT();
  const [scope, setScope] = useState<MailScope>('mine');
  const [label, setLabel] = useState('');
  const [q, setQ] = useState('');
  const [read, setRead] = useState<Record<number, true>>({});
  const [picked, setPicked] = useState<Record<number, true>>({});

  const list = useMemo(() => queryMails(MAILS, { me, scope, label, q }), [me, scope, label, q]);
  const counts = mailScopeCounts(MAILS, me);
  const labels = mailLabelCounts(MAILS);
  const live = MAILS.filter((m) => m.kind !== 'none');
  const over = live.filter((m) => m.slaMin < 0).length;
  const unN = counts.un;

  if (!open) {
    return (
      <aside className={`${s.pane} ${s.rail}`} aria-label={t('booking.a11y34')}>
        <button type="button" className={s.railBtn} aria-label={t('booking.a11y35')} title={t('booking.a11y36')} onClick={onToggle}>
          <IconChevronsRight size={16} aria-hidden="true" />
        </button>
        <button type="button" className={`${s.railBtn} ${s.railMail}`} aria-label={`info@ · ${live.length} thư mới`} title={`info@ · ${live.length} mới`} onClick={onToggle}>
          <IconMail size={18} aria-hidden="true" />
          <span className={s.railN}>{live.length}</span>
        </button>
        {unN > 0 && (
          <span className={`${s.railChip} ${s.railWarn}`} title={`PIC:UNASSIGNED · ${unN} thư`}>
            {unN}
          </span>
        )}
        {over > 0 && (
          <span className={`${s.railChip} ${s.railErr}`} title={`Quá SLA · ${over} thư`}>
            {over}
          </span>
        )}
      </aside>
    );
  }

  const pickedN = Object.keys(picked).length;
  return (
    <aside className={s.pane} aria-label={t('booking.a11y34')}>
      <div className={s.top}>
        <div className={s.title}>
          <IconMail size={18} aria-hidden="true" />
          <b>info@</b>
          <span className={s.sum} title={t('booking.a11y37')}>
            {live.length} mới · <span className={over ? s.over : undefined}>{over} quá SLA</span>
          </span>
          <span className={s.grow} />
          <button type="button" className={s.txtBtn} title={t('booking.a11y38')} onClick={onWrite}>
            <IconClockHour4 size={14} aria-hidden="true" />
            SLA
          </button>
          <button type="button" className={s.sq} aria-label={t('booking.a11y39')} title={t('booking.a11y39')} onClick={onWrite}>
            <IconRefresh size={14} aria-hidden="true" />
          </button>
          <button type="button" className={s.sq} aria-label={t('booking.a11y40')} title={t('booking.a11y41')} onClick={onToggle}>
            <IconChevronsLeft size={14} aria-hidden="true" />
          </button>
        </div>
        <label className={s.search}>
          <IconSearch size={14} aria-hidden="true" />
          <input aria-label={t('booking.a11y42')} placeholder={t('booking.a11y43')} value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className={s.scopes}>
          <Segmented
            kind="toggle"
            label="Lọc thư theo người"
            value={scope}
            onChange={setScope}
            items={SCOPES.map((x) => ({ key: x.key, label: `${x.label} ${counts[x.key]}` }))}
          />
        </div>
        <div className={s.labels} role="group" aria-label={t('booking.a11y44')}>
          {[['', MAILS.length] as [string, number], ...labels].map(([k, n]) => (
            <span key={k || 'all'} className={`${s.lbl} ${label === k ? s.lblOn : ''}`}>
              <button type="button" aria-pressed={label === k} title={`Lọc nhãn ${k || 'Tất cả'}`} onClick={() => setLabel(k)}>
                {k || 'Tất cả'} {n}
              </button>
              {k && (
                <button type="button" className={s.watch} aria-label={`Theo dõi nhãn ${k}`} title={`Theo dõi nhãn ${k}`} onClick={onWrite}>
                  <IconStar size={12} aria-hidden="true" />
                </button>
              )}
            </span>
          ))}
        </div>
        {pickedN > 0 && (
          <div className={s.bulk} role="toolbar" aria-label={t('booking.a11y45')}>
            <b>{pickedN} thư</b>
            <button type="button" className={s.txtBtn} onClick={() => setPicked({})}>
              {t('booking.deselect')}
            </button>
          </div>
        )}
      </div>
      <div className={s.scroll}>
        {list.map((m) => {
          const job = mailJob(m, requests);
          const ty = typeOf(m);
          const overdue = m.slaMin < 0;
          const un = isUnMail(m);
          return (
            <div key={m.i} className={`${s.mail} ${read[m.i] ? s.read : ''}`}>
              <div className={s.m1}>
                <input
                  type="checkbox"
                  aria-label={`Chọn thư ${m.subj}`}
                  checked={!!picked[m.i]}
                  onChange={() => setPicked((p) => (p[m.i] ? Object.fromEntries(Object.entries(p).filter(([k]) => +k !== m.i)) : { ...p, [m.i]: true }))}
                />
                <button type="button" className={s.open} aria-label={`Mở thư ${m.subj}`} onClick={() => setRead((r) => ({ ...r, [m.i]: true }))}>
                  <span className={s.from}>
                    <span className={s.dot} style={{ background: m.label === 'URGENT_CASE' ? 'var(--badge)' : un ? 'var(--d-amber)' : 'transparent' }} />
                    <span className={s.fromTx}>{m.from}</span>
                    <span className={s.time}>{m.time}</span>
                  </span>
                  <span className={s.subj}>{m.subj}</span>
                  <span className={s.pv}>{preview(m)}</span>
                </button>
              </div>
              <div className={s.tags}>
                <span className={s.tag} title={`Loại ${ty} · ${L_TIP[ty]}`}>
                  L{ty}
                </span>
                <span className={s.tag} style={{ background: `color-mix(in srgb, ${labelColor(m.label)} 14%, transparent)`, color: 'var(--tx)' }}>
                  {m.label}
                </span>
                {un && (
                  <span className={`${s.tag} ${s.tagWarn}`} title={t('booking.a11y46')}>
                    PIC:UNASSIGNED
                  </span>
                )}
                {m.kh && (
                  <span className={s.tag} title={`Khách hàng ${m.kh}`}>
                    <IconBuilding size={12} aria-hidden="true" />
                    {m.kh}
                  </span>
                )}
                {mailHasSla(m) && (
                  <span
                    className={`${s.tag} ${overdue ? s.tagErr : m.slaMin <= 15 ? s.tagWarn : ''}`}
                    title={overdue ? `SLA quá ${-m.slaMin} phút` : `SLA còn ${m.slaMin} phút`}
                    aria-label={overdue ? `SLA quá ${-m.slaMin} phút` : `SLA còn ${m.slaMin} phút`}
                  >
                    <IconClockHour4 size={12} aria-hidden="true" />
                    {overdue ? `−${-m.slaMin}′` : `${m.slaMin}′`}
                  </span>
                )}
                {m.atts > 0 && (
                  <span className={s.att} title={t('booking.a11y47')}>
                    <IconPaperclip size={12} aria-hidden="true" />
                    {m.atts}
                  </span>
                )}
                {job && (
                  <button type="button" className={s.job} title={t('booking.a11y48')} onClick={() => onJob(job)}>
                    → {job}
                  </button>
                )}
                {!job && m.kind === 'req' && (
                  <button type="button" className={s.mk} title={t('booking.a11y49')} onClick={onNew}>
                    <IconClockHour4 size={14} aria-hidden="true" />
                    {t('booking.noJob')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {list.length === 0 && <div className={s.none}>{scope === 'solved' ? 'Chưa có thư Solved' : 'Không có thư khớp'}</div>}
      </div>
    </aside>
  );
}
